import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import CustomersPage from "@/pages/CustomersPage";
import VouchersPage from "@/pages/VouchersPage";
import WhatsAppLogsPage from "@/pages/WhatsAppLogsPage";
import ReportsPage from "@/pages/ReportsPage";
import BrandsPage from "@/pages/BrandsPage";
import UsersPage from "@/pages/UsersPage";
import VoucherImagesPage from "@/pages/VoucherImagesPage";
import BrandDashboardPage from "@/pages/BrandDashboardPage";
import LiveDataPage from "@/pages/LiveDataPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Axios interceptor - ensures token is sent with EVERY request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("kw_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("kw_user");
    const token = localStorage.getItem("kw_token");
    if (stored && token) {
      try {
        setUser(JSON.parse(stored));
      } catch {}
    }
    setIsLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem("kw_user", JSON.stringify(userData));
    localStorage.setItem("kw_token", token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("kw_user");
    localStorage.removeItem("kw_token");
    localStorage.removeItem("kw_auth");
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const isBrand = user?.role === "brand";

  return (
    <AuthContext.Provider value={{ user, handleLogout }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={user ? <Navigate to="/" replace /> : <LoginPage onLogin={handleLogin} />}
            />
            <Route
              path="/*"
              element={
                user ? (
                  <DashboardLayout onLogout={handleLogout} user={user}>
                    <Routes>
                      {/* Brand users only see customers and vouchers */}
                      {isBrand ? (
                        <>
                          <Route index element={<BrandDashboardPage />} />
                          <Route path="customers" element={<CustomersPage />} />
                          <Route path="vouchers" element={<VouchersPage />} />
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                      ) : (
                        <>
                          <Route index element={<DashboardPage />} />
                          <Route path="customers" element={<CustomersPage />} />
                          <Route path="vouchers" element={<VouchersPage />} />
                          <Route path="brands" element={<BrandsPage />} />
                          <Route path="whatsapp" element={<WhatsAppLogsPage />} />
                          <Route path="reports" element={<ReportsPage />} />
                          <Route path="analytics" element={<AnalyticsPage />} />
                          <Route path="live-data" element={<LiveDataPage />} />
                          {isAdmin && <Route path="users" element={<UsersPage />} />}
                          {isAdmin && <Route path="voucher-images" element={<VoucherImagesPage />} />}
                        </>
                      )}
                    </Routes>
                  </DashboardLayout>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
